import { v4 as uuid } from "uuid";

import { type SupportRequestAttachment } from "@fitness-app/data-models";
import { TeamMemberStatus } from "@fitness-app/data-models/entities/TeamMember";

import { supabase } from "~/store/initializeStore";

export const SUPPORT_STORAGE_BUCKET = "support";
// ~20 years, mirrors the signed-url convention used elsewhere in the admin app.
export const SIGNED_URL_TTL_SECONDS = 631138519;

const getLiveSupportTrainerId = async (): Promise<string> => {
  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError || !user) {
    throw new Error(userError?.message ?? "Nie udało się potwierdzić użytkownika załącznika.");
  }

  const { data: member, error: memberError } = await supabase
    .from("team_member")
    .select("trainer, status, disabled")
    .eq("userId", user.id)
    .maybeSingle();

  if (memberError) {
    throw new Error(memberError.message);
  }

  if (!member || member.status !== TeamMemberStatus.ACTIVE || member.disabled === true) {
    throw new Error("Brak aktywnego członkostwa dla załącznika supportu.");
  }

  return member.trainer;
};

// Shared by the create modal and the drawer: upload to the support bucket and
// return the attachment row that support_request.attachments stores.
export const uploadSupportAttachment = async (file: File): Promise<SupportRequestAttachment> => {
  const supportTrainerId = await getLiveSupportTrainerId();
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
  const path = `${uuid()}-${safeName}`;

  const { error: uploadError } = await supabase.storage.from(SUPPORT_STORAGE_BUCKET).upload(path, file, {
    cacheControl: "3600",
    metadata: { supportTrainerId },
    upsert: true,
  });

  if (uploadError) {
    throw new Error(uploadError.message);
  }

  const { data, error: signError } = await supabase.storage
    .from(SUPPORT_STORAGE_BUCKET)
    .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);

  if (signError || !data?.signedUrl) {
    throw new Error(signError?.message || "Nie udało się podpisać adresu URL");
  }

  return { name: file.name, url: data.signedUrl, mimeType: file.type || undefined };
};
